export type UploadValidationKind = "avatar" | "editor-image" | "media-manager";

type MediaCategory = "image" | "video";

interface UploadRules {
  maxBytes: number;
  allowImage: boolean;
  allowVideo: boolean;
  allowSvg: boolean;
}

const IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/apng",
  "image/avif",
] as const;

const SVG_MIME_TYPE = "image/svg+xml";

const VIDEO_MIME_TYPES = [
  "video/mp4",
  "video/mpeg",
  "video/quicktime",
  "video/x-msvideo",
  "video/webm",
  "video/ogg",
  "video/3gpp",
  "video/x-flv",
] as const;

const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".apng", ".avif"] as const;
const SVG_EXTENSION = ".svg";
const VIDEO_EXTENSIONS = [".mp4", ".mpeg", ".mov", ".avi", ".webm", ".ogg", ".3gp", ".flv"] as const;

const RULES: Record<UploadValidationKind, UploadRules> = {
  avatar: {
    maxBytes: 10 * 1024 * 1024,
    allowImage: true,
    allowVideo: false,
    allowSvg: true,
  },
  "editor-image": {
    maxBytes: 20 * 1024 * 1024,
    allowImage: true,
    allowVideo: true,
    allowSvg: true,
  },
  "media-manager": {
    maxBytes: 1000 * 1024 * 1024,
    allowImage: true,
    allowVideo: true,
    allowSvg: true,
  },
};

const getFileExtension = (filename: string): string => {
  const normalized = filename.trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === normalized.length - 1) {
    return "";
  }
  return normalized.slice(dotIndex);
};

const getAllowedMimes = (rules: UploadRules): string[] => {
  const values: string[] = [];
  if (rules.allowImage) {
    values.push(...IMAGE_MIME_TYPES);
    if (rules.allowSvg) {
      values.push(SVG_MIME_TYPE);
    }
  }
  if (rules.allowVideo) {
    values.push(...VIDEO_MIME_TYPES);
  }
  return values;
};

const getAllowedExtensions = (rules: UploadRules): string[] => {
  const values: string[] = [];
  if (rules.allowImage) {
    values.push(...IMAGE_EXTENSIONS);
    if (rules.allowSvg) {
      values.push(SVG_EXTENSION);
    }
  }
  if (rules.allowVideo) {
    values.push(...VIDEO_EXTENSIONS);
  }
  return values;
};

export interface UploadValidationResult {
  valid: boolean;
  message: string;
  category?: MediaCategory;
}

export const validateUploadFile = (file: File, kind: UploadValidationKind): UploadValidationResult => {
  const rules = RULES[kind];
  const extension = getFileExtension(file.name);
  const allowedMimes = getAllowedMimes(rules);
  const allowedExtensions = getAllowedExtensions(rules);
  const mimeAllowed = allowedMimes.includes(file.type);
  const extensionAllowed = allowedExtensions.includes(extension);

  if (file.size <= 0) {
    return { valid: false, message: "File is empty." };
  }

  if (file.size > rules.maxBytes) {
    const maxMb = Math.floor(rules.maxBytes / (1024 * 1024));
    return { valid: false, message: `File is too large. Maximum size is ${maxMb}MB.` };
  }

  if (!mimeAllowed || !extensionAllowed) {
    const allowedExtensionsLabel = allowedExtensions.join(", ");
    return {
      valid: false,
      message: `Unsupported file type. Allowed types: ${allowedExtensionsLabel}.`,
    };
  }

  const category: MediaCategory = file.type.startsWith("video/") ? "video" : "image";
  return { valid: true, message: "", category };
};

export const getUploadAccept = (kind: UploadValidationKind): string => {
  const rules = RULES[kind];
  return getAllowedExtensions(rules).join(",");
};

